import { useMemo } from "react";

import { ParametersGeoCoordinatesTypeEnum } from "@/lib/api";
import type {
  ParameterTimeSeriesChartResult,
  ParameterTimeSeriesConfiguration,
  ParametersLocation,
} from "@/lib/api";
import type { TimeSeriesConfig } from "@/components/graph/graph.types";
import { useStationTimeseriesChartData } from "@/features/shared/widgets/observations-map/use-station-timeseries-chart-data";
import { mergeStationTimeseriesChartData } from "@/features/shared/widgets/observations-map/merge-station-timeseries-chart-data";

import { useModeledSeriesChartData } from "./use-modeled-series-chart-data";

const EMPTY_LOCATION: ParametersLocation = {
  id: "",
  name: "",
  coordinates: {
    type: ParametersGeoCoordinatesTypeEnum.Coordinates,
    latitude: 0,
    longitude: 0,
  },
  parameter_ids: [],
};
const EMPTY_CONFIGS: TimeSeriesConfig[] = [];
const EMPTY_CONFIGURATIONS: ParameterTimeSeriesConfiguration[] = [];
const EMPTY_PARAM_INDEX: Record<string, string> = {};
const EMPTY_LATLNG = { lat: 0, lng: 0 };

type UseTimeSeriesWidgetChartDataParams = {
  /** Resolved observed station; primary coordinate source for model series. */
  station: ParametersLocation | undefined;
  /**
   * Fallback coordinates for model series when no obs station is resolved but
   * the dashboard location itself carries a position (e.g. a location with only
   * model parameters and no linked weather station).
   */
  latlng?: { lat: number; lng: number };
  timeSeriesConfigs: TimeSeriesConfig[];
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[];
  /** `parameterId -> modelId`; empty for observation-only widgets (e.g. obs-map). */
  parameterModelIndex: Record<string, string>;
};

type TimeSeriesWidgetChartData = {
  /** One entry per configured series (config order), empty placeholders merged in. */
  chartData: ParameterTimeSeriesChartResult[];
  pending: boolean;
};

/**
 * Chart data for the standalone `time_series` widget, which can mix observed and
 * weather-model parameters in a single chart. Observed series come from the
 * station pipeline; model series are sampled at the station coordinate per each
 * model's latest run. Both sets merge into config order (empty placeholders for
 * configured-but-missing series), so the chart and legend stay complete.
 */
export function useTimeSeriesWidgetChartData({
  station,
  latlng,
  timeSeriesConfigs,
  timeSeriesConfigurations,
  parameterModelIndex,
}: UseTimeSeriesWidgetChartDataParams): TimeSeriesWidgetChartData {
  // Observed data requires a resolved station.
  const observedConfigs = station ? timeSeriesConfigs : EMPTY_CONFIGS;
  const observedConfigurations = station ? timeSeriesConfigurations : EMPTY_CONFIGURATIONS;

  // Model data only needs coordinates — use station coords first, then latlng fallback.
  const resolvedLatlng = useMemo(() => {
    if (station)
      return {
        lat: station.coordinates.latitude,
        lng: station.coordinates.longitude,
      };
    return latlng;
  }, [station, latlng]);
  const modelConfigs = resolvedLatlng ? timeSeriesConfigs : EMPTY_CONFIGS;
  const modelParamIndex = resolvedLatlng ? parameterModelIndex : EMPTY_PARAM_INDEX;

  const observed = useStationTimeseriesChartData({
    // Safe: observedConfigs is empty when station is undefined, so no queries fire.
    location: station ?? EMPTY_LOCATION,
    timeSeriesConfigs: observedConfigs,
    timeSeriesConfigurations: observedConfigurations,
  });

  const modeled = useModeledSeriesChartData({
    latlng: resolvedLatlng ?? EMPTY_LATLNG,
    timeSeriesConfigs: modelConfigs,
    parameterModelIndex: modelParamIndex,
  });

  const placeholderData = useMemo(
    () =>
      timeSeriesConfigs.flatMap((c) =>
        c.series.map((s) => ({
          parameter: { id: s.parameter_id, label: s.label ?? s.parameter_id },
          data: null as null,
        })),
      ),
    [timeSeriesConfigs],
  );

  const chartData = useMemo(() => {
    if (!station && !resolvedLatlng) return placeholderData;
    return mergeStationTimeseriesChartData(timeSeriesConfigs, [
      ...observed.chartResults,
      ...modeled.chartResults,
    ]);
  }, [
    station,
    resolvedLatlng,
    timeSeriesConfigs,
    observed.chartResults,
    modeled.chartResults,
    placeholderData,
  ]);

  return {
    chartData,
    pending:
      (station ? observed.pending : false) || (resolvedLatlng ? modeled.pending : false),
  };
}
